<template>
	<q-modal
		v-model="show_subscription_modal"
		:content-css="{ maxWidth: getMaxWidth, minWidth: getMinWidth }"
		no-backdrop-dismiss
		no-esc-dismiss
	>
		<q-modal-layout>
			<q-toolbar slot="header">
				<q-btn
					flat
					round
					dense
					@click="show_subscription_modal = false"
					icon="fas fa-times"
				/>
				<q-toolbar-title>
					{{ $t('registration_cancellation') }}
				</q-toolbar-title>
			</q-toolbar>

			<div class="layout-padding" v-if="user">
				<div class="col-12">
					<p>
						<strong>{{ $t('user') }}: </strong>{{ `${user.full_name} (${user.email})` }}
					</p>
				</div>

				<div class="col-12">
					<q-field :count="256">
						<q-input
							v-model="observation"
							ref="observation"
							:float-label="$t('motive')"
							:error="$v.observation.$error"
							@input="$v.observation.$touch"
							:disable="blockUserActions"
						/>
					</q-field>
				</div>

				<div class="col-12 q-mb-md">
					<div class="row gutter-sm">
						<div class="col-12">
							<p class="caption">{{ $t('evidences') }}</p>
							<uploader
								@fileUploaded="fileUploaded"
								:multiple="true"
								:cropper="false"
								:disable="blockUserActions"
							/>

							<q-list
								v-if="attachments.length"
								dense
								bordered
								padding
								separator
								class="q-mt-md"
							>
								<q-item v-for="(attachment, index) in attachments" :key="index">
									<q-item-side icon="fas fa-check" color="positive" />
									<q-item-main>
										<a :href="attachment" class="text-black" target="_blank">
											{{ $truncateLink(attachment) }}</a
										>
									</q-item-main>
									<q-item-side right>
										<q-btn
											round
											flat
											color="negative"
											icon="fas fa-trash"
											@click="removeFile(attachment)"
										>
											<q-tooltip>
												{{ $t('remove') }}
											</q-tooltip>
										</q-btn>
									</q-item-side>
								</q-item>
							</q-list>
						</div>
					</div>
				</div>

				<div class="row gutter-sm justify-end q-mt-lg">
					<div class="col-xs-12 col-sm-3" :class="getOrder('cancel')">
						<q-btn
							color="tertiary"
							class="full-width"
							:label="$t('cancel')"
							icon="fas fa-chevron-left"
							@click="show_subscription_modal = false"
						/>
					</div>
					<div class="col-xs-12 col-sm-3" :class="getOrder('save')">
						<q-btn
							color="secondary"
							class="full-width"
							:label="$t('save')"
							icon="far fa-save"
							@click="askForCancelation()"
							:disable="blockUserActions"
						>
							<q-tooltip v-if="!allow_enrollment_cancellation">
								{{ $t('enrollment_cancelation_limit_date_expired') }}
							</q-tooltip>
						</q-btn>
					</div>
				</div>

				<q-table
					v-if="logs.length"
					:data="logs"
					:columns="columns"
					row-key="id"
					:no-data-label="$t('no_data_avaliable')"
					:no-results-label="$t('no_results_found')"
					:loading-label="$t('loading_data')"
					:rows-per-page-label="$t('items_per_page')"
					:rows-per-page-options="[0]"
					class="q-mt-md"
					binary-state-sort
				>
					<q-tr slot="header" slot-scope="props" :props="props">
						<q-th v-for="col in props.cols" :key="col.name" :props="props">
							{{ $t(col.label) }}
						</q-th>
					</q-tr>

					<q-tr slot="body" slot-scope="props">
						<q-td key="author_full_name" :props="props">
							{{ props.row.author_full_name }}
						</q-td>

						<q-td key="author_email" :props="props">
							{{ props.row.author_email }}
						</q-td>

						<q-td key="status" :props="props">
							{{ $t(props.row.status) }}
						</q-td>

						<q-td key="observation" :props="props">
							{{ props.row.observation }}
						</q-td>

						<q-td key="created_date" :props="props">
							{{ props.row.created_date }}
						</q-td>

						<q-td auto-width key="actions" :props="props">
							<q-btn
								round
								flat
								icon="fas fa-exclamation"
								color="secondary"
								@click="openEvidence(props.row)"
								:disabled="!props.row.attachments.length"
							>
								<q-tooltip>
									{{ $t('show_evidences') }}
								</q-tooltip>
							</q-btn>
						</q-td>
					</q-tr>
				</q-table>

				<q-dialog v-model="attachments_view">
					<span slot="title">{{ $t('evidences') }}</span>

					<div slot="body">
						<q-list dense bordered padding separator class="q-mt-md">
							<q-item v-for="(attachment, index) in attachments_to_view" :key="index">
								<q-item-side icon="fas fa-check" color="positive" />
								<q-item-main>
									<a :href="attachment" class="text-black" target="_blank">
										{{ $truncateLink(attachment) }}</a
									>
								</q-item-main>
							</q-item>
						</q-list>
					</div>
				</q-dialog>
			</div>
		</q-modal-layout>
	</q-modal>
</template>

<script>
import { mapGetters } from 'vuex'
import { date } from 'quasar'
import { required, maxLength } from 'vuelidate/lib/validators'
import Uploader from 'components/Uploader'

export default {
	name: 'AdminPresentialCancelSubscription',

	props: [
		'show_subscription_history',
		'presential_event_id',
		'user',
		'allow_enrollment_cancellation',
	],

	components: {
		uploader: Uploader,
	},

	data() {
		return {
			observation: '',
			attachments_view: false,
			attachments_to_view: null,
			attachments: [],
			has_logs: false,
			logs: [],
			columns: [
				{
					name: 'author_full_name',
					required: true,
					label: 'owner',
					align: 'left',
					field: 'author_full_name',
					sortable: false,
				},
				{
					name: 'author_email',
					required: true,
					label: 'owner_email',
					align: 'left',
					field: 'author_email',
					sortable: false,
				},
				{
					name: 'status',
					required: true,
					label: 'status',
					align: 'left',
					field: 'status',
					sortable: false,
				},
				{
					name: 'observation',
					required: true,
					label: 'motive',
					align: 'left',
					field: 'observation',
					sortable: false,
				},
				{
					name: 'created_date',
					required: true,
					label: 'date',
					align: 'left',
					field: 'created_date',
					sortable: false,
				},
				{
					name: 'actions',
					required: true,
					label: 'evidences',
					align: 'right',
					field: 'actions',
					sortable: false,
				},
			],
		}
	},

	computed: {
		...mapGetters({
			access_token: 'common/access_token',
		}),

		getHeader() {
			let headers = {}
			return (headers = {
				headers: {
					'content-type': 'application/json',
					authorization:
						'Basic ' +
						btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
					authorization: 'Bearer ' + this.access_token,
				},
			})
		},

		show_subscription_modal: {
			get() {
				return this.show_subscription_history
			},
			set() {
				this.$emit('hide_subscription_history', '')
			},
		},

		getMaxWidth() {
			if (this.$q.platform.is.mobile) return '100vw'
			else return '80vw'
		},

		getMinWidth() {
			if (!this.$q.platform.is.mobile) return '70vw'
		},

		blockUserActions() {
			if (
				this.user.status === 4 ||
				this.user.status === 5 ||
				!this.allow_enrollment_cancellation
			)
				return true
			else return false
		},
	},

	methods: {
		askForCancelation() {
			this.$v.$touch()

			if (!this.$v.$error) {
				this.$q.loading.show()

				let data = {
					guests: [this.user.user_id],
					attachments: this.attachments,
					observation: this.observation,
				}

				this.$axios
					.post(
						`/presential_manager/presential_event_seats/${
							this.presential_event_id
						}/del_invited_user/`,
						data,
						this.getHeader
					)
					.then(() => {
						this.$q.notify({
							message: this.$t('request_sent'),
							type: 'positive',
							icon: 'far fa-save',
						})

						this.$emit('save_cancellation_and_load_guests')
						this.$v.$reset()
						this.$q.loading.hide()
					})
					.catch((e) => {
						console.log(e)

						this.$q.notify({
							message: this.$t('error_sending_request'),
							type: 'negative',
							icon: 'fas fa-ban',
						})
						this.$q.loading.hide()
					})
			} else {
				this.$q.notify({
					message: this.$t('make_sure_that_all_fields_are_filled_in'),
					type: 'negative',
					icon: 'fas fa-exclamation-triangle',
					position: 'top',
				})
			}
		},

		buildHistory(logs) {
			this.logs = logs.map((e) => {
				return {
					...e,
					created_date: date.formatDate(e.created_date, 'DD/MM/YYYY HH:mm:ss'),
					status:
						e.status === 1
							? 'cancellation_reproved'
							: e.status === 4
							? 'awaiting_cancellation'
							: e.status === 5
							? 'enrollment_canceled'
							: '',
				}
			})
		},

		getOrder(name) {
			if (name == 'cancel') {
				if (this.$q.platform.is.mobile) return 'order-last'
				else return 'order-first'
			} else if (name == 'save') {
				if (this.$q.platform.is.mobile) return 'order-first'
				else return 'order-last'
			}
		},

		fileUploaded(url) {
			this.attachments.push(url)
		},

		removeFile(attachment) {
			this.attachments = this.attachments.filter((e) => e !== attachment)
		},

		openEvidence(el) {
			this.attachments_to_view = el.attachments.map((e) => e)
			this.attachments_view = true
		},
	},

	watch: {
		user: function() {
			if (
				this.user &&
				this.user.invited_and_deleted_logs &&
				this.user.invited_and_deleted_logs.length
			) {
				this.logs = []
				this.buildHistory(this.user.invited_and_deleted_logs)
			} else {
				this.observation = ''
				this.attachments = []
				this.logs = []
				this.attachments_to_view = null
			}
		},
	},

	validations() {
		return {
			observation: { required, maxLength: maxLength(256) },
		}
	},
}
</script>

<style></style>
